import { useEffect, useRef } from "react"
import { NavLink, useLocation } from "react-router-dom"

import { cn } from "@/lib/utils"

/**
 * Подтягивает активную вкладку в видимую часть полосы.
 *
 * Полоса прокручивается внутри себя (см. ниже), и на 360px вкладка в хвосте
 * ряда оказывается за правым краем: человек открывает `/scoring/local` и видит
 * обрезанное «Loc…» — раздел, в котором он находится, приходится искать
 * скроллом. Двигаем только `scrollLeft` самой полосы и только когда вкладка
 * действительно вышла за край, чтобы страница под ней не прыгала.
 *
 * Живёт здесь, а не в трёх layout'ах: полос вкладок в проекте три (Transactions,
 * Scoring Engine, Lists), и правка поведения в одной из копий молча не доходит
 * до соседних.
 */
export function useActiveTabInView<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const { pathname } = useLocation()

  useEffect(() => {
    const strip = ref.current
    const active = strip?.querySelector<HTMLElement>("[aria-current='page']")
    if (!strip || !active) return
    const bar = strip.getBoundingClientRect()
    const tab = active.getBoundingClientRect()
    // Запас в 8px — чтобы вкладка не прилипала к самому краю полосы.
    if (tab.right > bar.right) strip.scrollLeft += tab.right - bar.right + 8
    else if (tab.left < bar.left) strip.scrollLeft -= bar.left - tab.left + 8
  }, [pathname])

  return ref
}

/**
 * Полоса вкладок раздела.
 *
 * Та же, что у Scoring Engine и Lists, но отдельным компонентом: у тех двоих
 * вкладки живут в общем маршруте-обёртке (`ScoringLayout`, `ListsLayout`) и
 * рисуются один раз на раздел. У Transactions обёртки нет — список и заливка
 * стоят самостоятельными маршрутами, и полосу пришлось бы повторить дословно в
 * двух файлах. Два экземпляра одной разметки разъезжаются молча: поправленный
 * отступ на одной вкладке не доходит до соседней, и человек видит, как строка
 * подпрыгивает при переходе.
 *
 * На 360px ряд уезжает горизонтальным скроллом внутри себя, а не ломается на
 * две строки — как и у соседних разделов.
 */
export interface Tab {
  label: string
  path: string
  /** Реализованная вкладка. За остальными стоит подпись «не реализовано». */
  live?: boolean
}

export function TabStrip({ label, tabs }: { label: string; tabs: readonly Tab[] }) {
  const strip = useActiveTabInView<HTMLElement>()

  return (
    <nav ref={strip} aria-label={label} className="-mb-px flex items-end gap-1 overflow-x-auto">
      {tabs.map((tab) => (
        <NavLink
          key={tab.path}
          to={tab.path}
          // `end` — там, где адрес вкладки является началом адреса соседней:
          // иначе корневая вкладка раздела остаётся подсвеченной на всех
          // остальных. Считается по самому набору, а не проставляется руками:
          // забытый флаг у новой вкладки — ровно та ошибка, которую на экране
          // замечают последней.
          end={tabs.some((other) => other.path !== tab.path && other.path.startsWith(`${tab.path}/`))}
          className={({ isActive }) =>
            cn(
              "shrink-0 whitespace-nowrap border-b-2 px-2.5 pb-2 text-sm font-medium transition-colors duration-150 ease-snap",
              isActive
                ? "border-accent text-accent"
                : "border-transparent text-fg-muted hover:text-fg",
              tab.live === false && !isActive && "text-fg-subtle",
            )
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  )
}
